import React, { useState } from 'react';
import { startCheckout, openCustomerPortal } from '../lib/billing';

// ─── Paywall ────────────────────────────────────────────────────────────────
//
// Full-page takeover when v_company_billing_state.has_access === false.
// Mounted by App.js BEFORE any of the rest of the app renders, so the
// operator can't navigate around it.
//
// Two reachable scenarios:
//   1. Trial ended without conversion → "Your trial has ended" copy.
//   2. Operator canceled and the period_ends_at has already passed →
//      same view; copy adapts based on the current status.
//
// The Manage billing link uses the Customer Portal (which can re-add
// payment methods even on canceled subscriptions). If they have no
// stripe_customer_id (dev-mode case for companies 4 + 10) we surface
// the dev-mode-aware copy from create-portal-session.
//
// Visual treatment matches Login.js — same rounded-2xl card, blue-600
// CTA — so the signed-in operator hitting paywall doesn't experience
// jarring style drift.

export default function Paywall({ billing }) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const status = billing && billing.subscription_status;
  const trialEnded = status === 'canceled' || status === 'unpaid' || status === 'trialing';
  const headline = status === 'canceled' || status === 'unpaid'
    ? 'Your subscription has ended'
    : (status === 'trialing'
      ? 'Your free trial has ended'
      : 'Start your free trial');
  const subhead = status === 'canceled' || status === 'unpaid'
    ? 'Resubscribe to continue using Posted.'
    : (trialEnded
      ? 'Add a payment method to continue using Posted.'
      : '30 days free. No charge until you decide to subscribe.');

  async function pickPlan(plan) {
    setError('');
    setLoading(true);
    await startCheckout(plan, {
      onError: (r) => setError(prettyError(r)),
    });
    setLoading(false);
  }

  async function manageBilling() {
    setError('');
    setLoading(true);
    await openCustomerPortal({
      onError: (r) => setError(prettyError(r)),
    });
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="text-2xl font-medium text-gray-900 mb-1">Posted</div>
          <div className="text-xs text-gray-300">86'd the whiteboard.</div>
        </div>

        <div className="text-center mb-6">
          <div className="text-xl font-semibold text-gray-900 mb-2">{headline}</div>
          <div className="text-sm text-gray-500">{subhead}</div>
        </div>

        <div className="border border-gray-100 rounded-xl p-4 mb-5 bg-gray-50">
          <div className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2">Posted helps you</div>
          <ul className="text-sm text-gray-700 leading-relaxed space-y-1">
            <li>Watch food costs in real-time as invoices arrive.</li>
            <li>Catch price increases before they hit your P&amp;L.</li>
            <li>Generate ready-to-send distributor outreach drafts.</li>
          </ul>
        </div>

        {error && (
          <div className="mb-4 text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <button
            type="button"
            disabled={loading}
            onClick={() => pickPlan('monthly')}
            className="bg-blue-600 text-white rounded-lg py-3 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
          >
            Subscribe Monthly · $39/mo
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => pickPlan('annual')}
            className="bg-gray-900 text-white rounded-lg py-3 text-sm font-semibold hover:bg-black disabled:opacity-50"
          >
            Subscribe Annually · $390/yr
            <div className="text-[11px] font-normal opacity-80 mt-0.5">save 17%</div>
          </button>
        </div>

        <div className="text-center">
          <button
            type="button"
            disabled={loading}
            onClick={manageBilling}
            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: loading ? 'wait' : 'pointer', font: 'inherit' }}
          >
            Need to update your payment method? Manage billing
          </button>
        </div>
      </div>
    </div>
  );
}

// Pretty-print API failures into operator-readable copy.
function prettyError(result) {
  if (!result) return 'Something went wrong. Try again in a moment.';
  if (result.error === 'network') return 'Couldn\'t connect — check your internet and try again.';
  if (result.error === 'stripe_unavailable') return 'Stripe is unreachable right now. Try again in a moment.';
  if (result.error === 'no_subscription_yet') {
    return 'You don\'t have a subscription yet — pick a plan above to start.';
  }
  if (result.error === 'no_session') return 'Your session expired. Sign in again.';
  return (result.body && result.body.message) || 'Something went wrong. Try again in a moment.';
}
